import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, CheckCircle, AlertCircle, X, Play } from 'lucide-react';

interface GeneratingModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onError?: (message: string) => void;
  onClose?: () => void;
  onValidate?: () => Promise<{ enough: boolean; error?: string }>;
  singleChoiceCount: number;
  judgeCount: number;
  programCount: number;
}

export default function GeneratingModal({
  isOpen,
  onComplete,
  onError,
  onClose,
  onValidate,
  singleChoiceCount,
  judgeCount,
  programCount
}: GeneratingModalProps) {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('正在分析题库...');
  const [isComplete, setIsComplete] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const validatedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setProgress(0);
      setStatusText('正在分析题库...');
      setIsComplete(false);
      setShowError(false);
      setIsCancelled(false);
      setErrorMsg('');
      validatedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      return;
    }

    if (isCancelled) return;

    // 异步校验题目数量
    if (onValidate && !validatedRef.current) {
      validatedRef.current = true;
      setStatusText('正在校验题库...');
      onValidate().then(result => {
        if (isCancelled) return;
        if (!result.enough) {
          setShowError(true);
          setErrorMsg(result.error || '题目不足，无法组卷');
          setStatusText(result.error || '题目不足，无法组卷');
          autoCloseTimerRef.current = setTimeout(() => {
            if (onError) onError(result.error || '题目不足，无法组卷');
          }, 1500);
          return;
        }
        startProgressAnimation();
      });
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      };
    }

    // 无校验回调时直接开始动画
    if (!onValidate) {
      startProgressAnimation();
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    };
  }, [isOpen, isCancelled]);

  const startProgressAnimation = () => {
    if (isCancelled) return;
    const duration = 1500;
    const interval = 50;
    const steps = duration / interval;
    let currentStep = 0;

    timerRef.current = setInterval(() => {
      if (isCancelled) return;
      currentStep++;
      const newProgress = Math.min((currentStep / steps) * 100, 100);
      setProgress(newProgress);

      if (newProgress < 20) {
        setStatusText('正在分析题库...');
      } else if (newProgress < 40) {
        setStatusText('正在从题库中随机抽题...');
      } else if (newProgress < 60) {
        setStatusText(`正在抽取单选题 (${singleChoiceCount}道)...`);
      } else if (newProgress < 80) {
        setStatusText(`正在抽取判断题 (${judgeCount}道)...`);
      } else if (newProgress < 95) {
        setStatusText(`正在抽取编程题 (${programCount}道)...`);
      } else {
        setStatusText('正在生成试卷...');
      }

      if (newProgress >= 100) {
        if (timerRef.current) clearInterval(timerRef.current);
        setIsComplete(true);
        setStatusText('组卷完成！');
      }
    }, interval);
  };

  const handleClose = () => {
    setIsCancelled(true);
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    if (onClose) {
      onClose();
    }
  };

  const handleStartExam = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    onComplete();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl w-full max-w-md overflow-hidden p-8 relative"
        >
          {/* 关闭按钮 */}
          {!isComplete && !showError && (
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="中断组卷"
            >
              <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}

          <div className="text-center mb-6">
            {showError ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <AlertCircle className="w-8 h-8 text-red-600" />
              </motion.div>
            ) : isComplete ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle className="w-8 h-8 text-green-600" />
              </motion.div>
            ) : (
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
            )}
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {showError ? '组卷失败' : isComplete ? '组卷完成' : '组卷进行中'}
            </h2>
            <p className={`text-sm ${showError ? 'text-red-500' : 'text-gray-500'}`}>{showError ? errorMsg : statusText}</p>
          </div>

          {!showError && (
            <div className="space-y-4">
              <div className="flex justify-between text-sm text-gray-600">
                <span>单选题</span>
                <span className="font-medium">{singleChoiceCount}道</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>判断题</span>
                <span className="font-medium">{judgeCount}道</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>编程题</span>
                <span className="font-medium">{programCount}道</span>
              </div>

              <div className="mt-6">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-blue-600 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.1 }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-sm text-gray-500">
                  <span>进度</span>
                  <span>{Math.round(progress)}%</span>
                </div>
              </div>

              {/* 完成后的开始答题按钮 */}
              {isComplete && (
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={handleStartExam}
                  className="w-full mt-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  开始答题
                </motion.button>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
